/**
 * Common utility functions for astrology calculations
 * Shared across all divisional chart calculations
 */

import { devWarn } from '../../../../../utils/devLog';
import { JatakaResponse } from '../../../../../models/api';

/**
 * Parse longitude value from position string
 * Handles various formats like "15° 23' 45\" Aries" or "195.396"
 */
export function parseLongitudeFromPosition(position: string): number {
    // Simple longitude extraction - extract the first number found
    const match = position.match(/(\d+(?:\.\d+)?)/);
    if (match) {
        return parseFloat(match[1]);
    }
    devWarn(`Failed to parse longitude from position: ${position}`);
    return 0;
}

/**
 * Get Surya (Sun) longitude from API response
 * Used as reference point for various calculations
 */
export function getSuryaLongitudeFromApi(apiResponse: JatakaResponse): number {
    const suryaPlanet = apiResponse.graha_positions?.planets?.find(p => p.short_name === 'Sy');
    if (!suryaPlanet) {
        devWarn('Surya not found in planets, using longitude 0');
        return 0;
    }
    return parseLongitudeFromPosition(suryaPlanet.position);
}

/**
 * Get current Hora planet from API response
 * Used for highlighting current Hora planet in calculations
 */
export function getCurrentHoraPlanet(apiResponse: JatakaResponse): string | null {
    const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;
    return currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : null;
}

/**
 * Check if planet is the current Hora planet
 */
export function isPlanetHora(planet: any, apiResponse: JatakaResponse): boolean {
    const horaPlanetName = getCurrentHoraPlanet(apiResponse);
    return !!(horaPlanetName && (
        planet.short_name === horaPlanetName ||
        planet.name === horaPlanetName
    ));
}

/**
 * Calculate which D8 part (1-8) a longitude falls into
 */
export function getLongitudeD8Part(longitude: number, sunriseLongitude: number = 0): number {
    const adjustedLongitude = ((longitude - sunriseLongitude + 360) % 360);
    return Math.floor(adjustedLongitude / 45) + 1; // Each part is 45° (360°/8)
}

/**
 * Calculate which D12 part (1-12) a longitude falls into relative to Surya
 */
export function getLongitudeD12Part(planetLongitude: number, suryaLongitude: number = 0): number {
    const adjustedLongitude = ((planetLongitude - suryaLongitude + 360) % 360);
    // Each part is 30° (360°/12), but we want degrees within the 12° segment to determine quality
    const degreeInPart = adjustedLongitude % 12;
    return Math.floor(degreeInPart) + 1; // 1-12
}

/**
 * Calculate which Nakshatra (1-27) a longitude falls into
 */
export function getNakshatraIndex(longitude: number): number {
    return Math.floor(longitude / (360 / 27)) + 1; // Each nakshatra is ~13.33° (360°/27)
}

/**
 * Create display text for planet with retrograde indication
 */
export function createPlanetDisplayText(planet: any): string {
    if (planet.is_retrograde && planet.short_name !== 'Lg') {
        return `(${planet.short_name})`; // Wrap in parentheses for retrograde
    }
    return planet.short_name;
}

/**
 * Create standardized planet display object
 */
export interface PlanetDisplay {
    shortName: string;
    isRetrograde: boolean;
    isHora: boolean;
    isChandra: boolean;
    isLagna: boolean;
    displayText: string;
}

export function createPlanetDisplay(planet: any, apiResponse: JatakaResponse): PlanetDisplay {
    const isLagna = planet.short_name === 'Lg';
    const isChandra = planet.short_name === 'Ch';
    const isRetrograde = planet.is_retrograde || false;
    const isHora = isPlanetHora(planet, apiResponse);

    return {
        shortName: planet.short_name,
        isRetrograde,
        isHora,
        isChandra,
        isLagna,
        displayText: createPlanetDisplayText(planet)
    };
}